// src/shared/context/DeviceContext.js
// React Context for device state management

import React, { createContext, useContext, useMemo } from 'react';
import { useDevice, DEVICE_TYPES, BREAKPOINTS } from '../hooks/useDevice';

// Create context with undefined default (requires provider)
const DeviceContext = createContext(undefined);

/**
 * DeviceProvider - Wraps the app to provide device state
 * Place this at the root of your app (in App.js)
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const DeviceProvider = ({ children }) => {
  const device = useDevice();

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      ...device,
      // Additional computed values
      isSmallMobile: device.width <= 375,
      isLargeMobile: device.width > 375 && device.width <= BREAKPOINTS.mobile,
      isPortrait: device.orientation === 'portrait',
      isLandscape: device.orientation === 'landscape',
    }),
    [device]
  );

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
};

/**
 * useDeviceContext - Hook to access device context
 * Must be used within a DeviceProvider
 *
 * @returns {Object} Device context value
 * @throws {Error} If used outside of DeviceProvider
 */
export const useDeviceContext = () => {
  const context = useContext(DeviceContext);

  if (context === undefined) {
    throw new Error('useDeviceContext must be used within a DeviceProvider');
  }

  return context;
};

// Re-export constants for convenience
export { DEVICE_TYPES, BREAKPOINTS };

export default DeviceContext;
